import {createSlice} from '@reduxjs/toolkit';
import {
    getYoySummaryData,
    getMtdSummaryData,
    getYoyGraphsData,
    getMtdGraphData,
    getYoyBreakdownData,
    getMtdBreakdownData,
} from './dashboard-actions';

/**
 *
 * @typedef {object} TDashboardSliceInitialState
 * @property {object} yoy
 * @property {object} yoy.summary - Data for the dashboard summary cards
 * @property {object} yoy.summary.revenue - Revenue data for the dashboard summary cards
 * @property {string} yoy.summary.revenue.value - Revenue value for the dashboard summary cards
 * @property {string} yoy.summary.revenue.previous - Previous revenue value for the dashboard summary cards
 * @property {number} yoy.summary.revenue.diff - Revenue diff for the dashboard summary cards
 * @property {object} yoy.summary.reservations - Reservations data for the dashboard summary cards
 * @property {string} yoy.summary.reservations.value - Reservations value for the dashboard summary cards
 * @property {string} yoy.summary.reservations.previous - Previous reservations value for the dashboard summary cards
 * @property {number} yoy.summary.reservations.diff - Reservations diff for the dashboard summary cards
 * @property {object} yoy.graphs - Data for the dashboard graphs
 * @property {Array} yoy.graphs.revenue - Revenue data for the dashboard graphs
 * @property {Array} yoy.graphs.reservations - Reservations data for the dashboard graphs
 * @property {object} mtd
 * @property {object} mtd.summary - Data for the dashboard summary cards
 * @property {object} mtd.summary.revenue - Revenue data for the dashboard summary cards
 * @property {string} mtd.summary.revenue.value - Revenue value for the dashboard summary cards
 * @property {string} mtd.summary.revenue.previous - Previous revenue value for the dashboard summary cards
 * @property {number} mtd.summary.revenue.diff - Revenue diff for the dashboard summary cards
 * @property {object} mtd.summary.reservations - Reservations data for the dashboard summary cards
 * @property {string} mtd.summary.reservations.value - Reservations value for the dashboard summary cards
 * @property {string} mtd.summary.reservations.previous - Previous reservations value for the dashboard summary cards
 * @property {number} mtd.summary.reservations.diff - Reservations diff for the dashboard summary cards
 * @property {object} mtd.graphs - Data for the dashboard graphs
 * @property {object} mtd.graphs.x - Data for a 'current' month on the dashboard graphs
 * @property {Array} mtd.graphs.x.data - An array of objects representing the data for a 'current' month on the dashboard graphs
 * @property {string} mtd.graphs.x.id - The title/id for a 'current' month on the dashboard graphs
 * @property {object} mtd.graphs.y - Data for a 'previous' month on the dashboard graphs
 * @property {Array} mtd.graphs.y.data - An array of objects representing the data for a 'previous' month on the dashboard graphs
 * @property {string} mtd.graphs.y.id - The title/id for a 'previous' month on the dashboard graphs
 */
export const initialState = {
    mtd: {
        summary: {
            revenue: {
                value: '0',
                diff: 0,
                previousValue: '0',
            },
            reservations: {
                value: '0',
                diff: 0,
                previousValue: '0',
            },
            isLoading: false,
            error: null,
        },
        graphs: {
            x: {
                data: [],
                id: '',
            },
            y: {
                data: [],
                id: '',
            },
            isLoading: false,
            error: null,
        },
        breakdown: {
            isLoading: false,
            error: null,
            data: [],
        },
    },
    yoy: {
        summary: {
            revenue: {
                value: '0',
                diff: 0,
                previousValue: '0',
            },
            reservations: {
                value: '0',
                diff: 0,
                previousValue: '0',
            },
            isLoading: false,
            error: null,
        },
        graphs: {
            revenue: [],
            reservations: [],
            isLoading: false,
            error: null,
        },
        breakdown: {
            isLoading: false,
            error: null,
            data: [],
        },
    },
};

export const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getYoySummaryData.pending, (state) => {
                state.yoy.summary.isLoading = true;
                state.yoy.summary.error = null;
            })
            .addCase(getYoySummaryData.fulfilled, (state, action) => {
                state.yoy.summary = action.payload;
                state.yoy.summary.isLoading = false;
                state.yoy.summary.error = null;
            })
            .addCase(getYoySummaryData.rejected, (state, action) => {
                state.yoy.summary.isLoading = false;
                state.yoy.summary.error = action.payload;
            })

            .addCase(getYoyGraphsData.pending, (state) => {
                state.yoy.graphs = {...state.yoy.graphs, isLoading: true};
                state.yoy.graphs.error = null;
            })
            .addCase(getYoyGraphsData.fulfilled, (state, action) => {
                state.yoy.graphs = {...action.payload, isLoading: false};
                state.yoy.graphs.error = null;
            })
            .addCase(getYoyGraphsData.rejected, (state, action) => {
                state.yoy.graphs = {...state.yoy.graphs, isLoading: false};
                state.yoy.graphs.error = action.payload;
            })

            .addCase(getYoyBreakdownData.pending, (state) => {
                state.yoy.breakdown.isLoading = true;
                state.yoy.breakdown.error = null;
            })
            .addCase(getYoyBreakdownData.fulfilled, (state, action) => {
                state.yoy.breakdown.data = action.payload;
                state.yoy.breakdown.isLoading = false;
                state.yoy.breakdown.error = null;
            })
            .addCase(getYoyBreakdownData.rejected, (state, action) => {
                state.yoy.breakdown = {
                    ...state.yoy.breakdown,
                    isLoading: false,
                };
                state.yoy.breakdown.error = action.payload;
            })

            .addCase(getMtdSummaryData.pending, (state) => {
                state.mtd.summary.isLoading = true;
                state.mtd.summary.error = null;
            })
            .addCase(getMtdSummaryData.fulfilled, (state, action) => {
                state.mtd.summary = action.payload;
                state.mtd.summary.isLoading = false;
                state.mtd.summary.error = null;
            })
            .addCase(getMtdSummaryData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }
                state.mtd.summary.isLoading = false;
                state.mtd.summary.error = action.payload;
            })

            .addCase(getMtdGraphData.pending, (state) => {
                state.mtd.graphs.isLoading = true;
                state.mtd.graphs.error = null;
            })
            .addCase(getMtdGraphData.fulfilled, (state, action) => {
                state.mtd.graphs = action.payload;
                state.mtd.graphs.isLoading = false;
                state.mtd.graphs.error = null;
            })
            .addCase(getMtdGraphData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }
                state.mtd.graphs.isLoading = false;
                state.mtd.graphs.error = action.payload;
            })

            .addCase(getMtdBreakdownData.pending, (state) => {
                state.mtd.breakdown.isLoading = true;
                state.mtd.breakdown.error = null;
            })
            .addCase(getMtdBreakdownData.fulfilled, (state, action) => {
                state.mtd.breakdown = action.payload;
                state.mtd.breakdown.isLoading = false;
                state.mtd.breakdown.error = null;
            })
            .addCase(getMtdBreakdownData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }
                state.mtd.breakdown.isLoading = false;
                state.mtd.breakdown.error = action.payload;
            });
    },
});

export default dashboardSlice.reducer;
